import { DiagnosticMsg, DiagTreeNode } from "../types/diag";
import { QueryType } from "../types/dialog-types";

// All controller-alarm severity bits (Mark/OPC/Sentinel) from pc_common.h.
const CONTROLLER_ALARM_MASK =
  0x100 | 0x200 | 0x400 | 0x800 | 0x1000 | 0x2000 | 0x4000 | 0x8000 | 0x10000 |
  0x20000 | 0x40000;

function isControllerAlarm(severity: number): boolean {
  return (severity & CONTROLLER_ALARM_MASK) !== 0;
}

/** Seconds-of-day parsed from a controller-alarm message's leading
 *  "HH:MM:SS.mmm" timestamp (mirrors C++ get_controller_alarm_time). */
function controllerAlarmTime(diagMessage: string): number {
  const m = /^(\d+):(\d+):(\d+)\.(\d+)/.exec(diagMessage);
  if (!m) return 0;
  return Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3]) + Number(m[4]) / 1000;
}

/** Time-ascending compare with the C++ controller-alarm tie-break: on equal
 *  diag_time, controller alarms sort first and order by their embedded time. */
function compareByTime(a: DiagnosticMsg, b: DiagnosticMsg): number {
  if (a.diag_time !== b.diag_time) return a.diag_time - b.diag_time;
  const ca = isControllerAlarm(a.severity);
  const cb = isControllerAlarm(b.severity);
  if (ca && !cb) return -1;
  if (!ca && cb) return 1;
  if (ca && cb) {
    return controllerAlarmTime(a.diag_message) - controllerAlarmTime(b.diag_message);
  }
  return 0;
}

/** Severity ascending (Trip = 0x01 first, Information = 0x20 last), then
 *  latch_count (the per-row occurrence count) descending so the most-frequent
 *  diagnostic appears at the top within each severity band. */
function compareByOccurrences(a: DiagTreeNode, b: DiagTreeNode): number {
  return a.severity !== b.severity
    ? a.severity - b.severity
    : b.latch_count - a.latch_count;
}

/** Occurrence count descending, then severity ascending — the C++
 *  SORT_BY_NUM_OCCURANCES_AND_SEVERITY. */
function compareByNumOccurrences(a: DiagTreeNode, b: DiagTreeNode): number {
  return b.latch_count !== a.latch_count
    ? b.latch_count - a.latch_count
    : a.severity - b.severity;
}

/** Message text alphabetical, then the severity/occurrence order as a
 *  tie-break (C++ SORT_BY_MESSAGE_TEXT). */
function compareByMessageText(a: DiagTreeNode, b: DiagTreeNode): number {
  return a.diag_message.localeCompare(b.diag_message) || compareByOccurrences(a, b);
}

/** Message id alphabetical, then severity/occurrence tie-break
 *  (C++ SORT_BY_MSG_ID). */
function compareByMsgId(a: DiagTreeNode, b: DiagTreeNode): number {
  return a.message_id.localeCompare(b.message_id) || compareByOccurrences(a, b);
}

/** The four sort modes offered for a Number-of-Occurrences query (the C++
 *  occurrences "Sort By" submenu). */
export type OccurrenceSort = "severity" | "occurrences" | "message" | "messageId";

function occurrenceComparator(
  sort: OccurrenceSort,
): (a: DiagTreeNode, b: DiagTreeNode) => number {
  switch (sort) {
    case "occurrences":
      return compareByNumOccurrences;
    case "message":
      return compareByMessageText;
    case "messageId":
      return compareByMsgId;
    case "severity":
    default:
      return compareByOccurrences;
  }
}

/**
 * Sort the *first* (non-latched) top-level rows of a diagnostic tree for
 * display.
 *
 * - "Number of Occurrences": by occurrences (severity ascending, then
 *   latch_count descending).
 * - All other query types: diag_time ascending (oldest first).
 *
 * The trailing "Latched Messages" section is sorted separately via
 * {@link sortRootsByOccurrences}.
 *
 * Returns a sorted copy; the original `roots` array is not mutated. Children
 * keep the order the backend supplied — they're typically chronological
 * within each parent and aren't covered by the user's sort spec.
 */
export function sortRootsForQuery(
  roots: DiagTreeNode[],
  queryType: QueryType,
  // Time-ordered queries can be flipped to newest-first via the "Sort By →
  // Reverse Time Order" menu (C++ SORT_BY_REVERSE_TIME_AND_TURBINE). Ignored
  // for Number-of-Occurrences.
  reverseTime = false,
  // Number-of-Occurrences queries choose among four orders via their own
  // "Sort By" submenu (severity / occurrences / message / message id). Ignored
  // for time-ordered queries.
  occSort: OccurrenceSort = "severity",
): DiagTreeNode[] {
  const sorted = [...roots];
  if (queryType === "Number of Occurrences") {
    sorted.sort(occurrenceComparator(occSort));
  } else {
    sorted.sort(reverseTime ? (a, b) => compareByTime(b, a) : compareByTime);
  }
  return sorted;
}

/**
 * Sort the latched-messages section by occurrences (severity ascending, then
 * latch_count descending), regardless of query type. Returns a sorted copy.
 */
export function sortRootsByOccurrences(roots: DiagTreeNode[]): DiagTreeNode[] {
  return [...roots].sort(compareByOccurrences);
}

/**
 * Orphans are by definition non-top-level (children whose parent isn't in the
 * result set), so they're always sorted by time (ascending), regardless of
 * query type.
 */
export function sortOrphansForQuery(
  orphans: DiagnosticMsg[],
  _queryType: QueryType,
): DiagnosticMsg[] {
  return [...orphans].sort(compareByTime);
}
